import { z } from "zod";
import { money, trimmed, uuid } from "./common";

/**
 * Банкеты и мероприятия (`addon.banquets`, ТЗ 09 §9.11): заявка → предварительно → подтверждён (только после депозита, BR-196)
 * → идёт (создан заказ из банкетного меню, депозит зачтён оплатой `deposit`) → завершён (заказ оплачен, BR-198) / отменён.
 * Подтверждённый банкет блокирует свои залы для обычных броней на время мероприятия (BR-197).
 */
export const BANQUET_STATUSES = ["inquiry", "tentative", "confirmed", "in_progress", "completed", "cancelled"] as const;
export type BanquetStatus = (typeof BANQUET_STATUSES)[number];
export const BANQUET_TYPES = ["wedding", "birthday", "corporate", "anniversary", "other"] as const;
export type BanquetType = (typeof BANQUET_TYPES)[number];
/** Блокируют залы и считаются в календаре занятости */
export const BANQUET_BLOCKING: BanquetStatus[] = ["confirmed", "in_progress"];
/** Куда можно перейти вручную (in_progress — только «Начать банкет», completed — «Завершить») */
export const BANQUET_TRANSITIONS: Record<BanquetStatus, BanquetStatus[]> = {
  inquiry: ["tentative", "confirmed", "cancelled"],
  tentative: ["inquiry", "confirmed", "cancelled"],
  confirmed: ["tentative", "cancelled"],
  in_progress: [],
  completed: [],
  cancelled: [],
};
export const DEPOSIT_METHODS = ["cash", "card", "bank"] as const;

const iso = z.iso.datetime({ offset: true, error: "Дата и время в формате ISO" });
const base = {
  name: trimmed(2, 120),
  type: z.enum(BANQUET_TYPES).default("other"),
  startsAt: iso,
  endsAt: iso,
  hallIds: z.array(uuid).max(10).default([]),
  guests: z.number().int().min(1).max(5000),
  customerId: uuid.nullable().optional(),
  managerId: uuid.nullable().optional(),
  /** Сколько внести, чтобы подтвердить (BR-196); 0 — без депозита */
  depositRequired: money.default(0),
  notes: trimmed(0, 2000).nullable().optional(),
  /** Доп. расходы мероприятия (декор, аниматор) — в прибыльности */
  extraCosts: z.array(z.strictObject({ name: trimmed(1, 120), amount: money })).max(50).default([]),
};
export const banquetSchema = z.strictObject(base).refine((b) => b.endsAt > b.startsAt, { error: "Окончание позже начала", path: ["endsAt"] });
export type BanquetInput = z.input<typeof banquetSchema>;
/** Изменение: только переданные поля (без умолчаний), с версией (409 при конфликте) */
export const banquetUpdateSchema = z
  .strictObject({
    name: base.name.optional(), type: z.enum(BANQUET_TYPES).optional(), startsAt: iso.optional(), endsAt: iso.optional(),
    hallIds: z.array(uuid).max(10).optional(), guests: base.guests.optional(), customerId: base.customerId, managerId: base.managerId,
    depositRequired: money.optional(), notes: base.notes, extraCosts: z.array(z.strictObject({ name: trimmed(1, 120), amount: money })).max(50).optional(),
    version: z.number().int().min(1),
  })
  .refine((b) => !b.startsAt || !b.endsAt || b.endsAt > b.startsAt, { error: "Окончание позже начала", path: ["endsAt"] });

export const banquetMenuSchema = z.strictObject({
  items: z.array(z.strictObject({
    menuItemId: uuid,
    /** На одного гостя (умножается на число гостей) или всего */
    perGuest: z.boolean().default(false),
    qty: z.number().positive().max(10_000),
    course: z.number().int().min(1).max(9).default(1),
    /** Цена за единицу для банкета; без неё — цена меню филиала */
    unitPrice: money.optional(),
  })).max(200),
});
export type BanquetMenuInput = z.input<typeof banquetMenuSchema>;
export const banquetStatusSchema = z.strictObject({ status: z.enum(BANQUET_STATUSES), reason: trimmed(1, 300).optional() });
export const banquetDepositSchema = z.strictObject({ amount: money.refine((v) => v > 0, { error: "Сумма больше нуля" }), method: z.enum(DEPOSIT_METHODS), comment: trimmed(0, 300).nullable().optional() });
export const banquetsQuery = z.object({
  from: z.iso.date().optional(), to: z.iso.date().optional(),
  status: z.enum(BANQUET_STATUSES).optional(),
});
